import { useAuth } from "@via/hooks/useAuth";
import {
  lookupFailedMessage,
  messageForResult,
  notFoundMessage,
  landingFor,
  resolveAlias,
} from "@via/pages/scan/scan.api";
import ScanUnavailable from "@via/pages/scan/scan-unavailable";
import { ROUTES } from "@via/utils/constants.tsx";
import { isScanAvailable } from "@via/utils/scan";
import { normalizeAliasCode } from "common";
import { type JSX, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export const ALIAS_PATH_PREFIX = "/a/";

/**
 * The code in a /a/<code> link, decoded exactly once from the raw path. Not from the route's
 * splat param: React Router decodes the path for matching and then turns %2F into / inside
 * params, which would read a literal "%2F" in a code as a slash. A malformed escape is no code.
 */
export const parseAliasPath = (
  pathname: string
): { raw: string; code: string | null } => {
  const raw = pathname.startsWith(ALIAS_PATH_PREFIX)
    ? pathname.slice(ALIAS_PATH_PREFIX.length)
    : "";
  try {
    return { raw, code: decodeURIComponent(raw) };
  } catch {
    return { raw, code: null };
  }
};

/** /a/<code>: resolve and replace the location with the record link, or explain the miss. */
export default function AliasRedirect(): JSX.Element {
  const { pathname } = useLocation();
  const { config } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const available = isScanAvailable(config);
  const { raw, code: decoded } = parseAliasPath(pathname);

  useEffect(() => {
    if (!available) return;
    setMessage(null);
    const code = normalizeAliasCode(decoded);
    if (code === null) {
      setMessage(notFoundMessage(decoded ?? raw));
      return;
    }
    // Leaving, or this link becoming another one, abandons the lookup: its answer is ignored.
    const controller = new AbortController();
    resolveAlias(code, { signal: controller.signal })
      .then(result => {
        if (controller.signal.aborted) return;
        if (result.kind === "found") {
          const landing = landingFor(result.target);
          navigate(landing.to, { replace: true, state: landing.state });
          return;
        }
        setMessage(messageForResult(result, code));
      })
      .catch(() => {
        if (!controller.signal.aborted) setMessage(lookupFailedMessage(code));
      });
    return () => controller.abort();
  }, [available, navigate, raw, decoded]);

  if (!config) return <main className="p-4" />;
  if (!available) return <ScanUnavailable />;
  return (
    <main className="p-4">
      {message ? (
        <>
          <p role="status" className="text-sm text-gray-800">
            {message}
          </p>
          <Link
            to={ROUTES.SCAN}
            className="mt-3 inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-500">
            Scan another code
          </Link>
        </>
      ) : (
        <p className="text-sm text-gray-500">Looking up {decoded ?? raw}</p>
      )}
    </main>
  );
}
